import { FC, HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { scalePop, strokeDraw } from '@animations/index';
import { EdnaWrapper } from '@providers/index';

export interface CheckmarkIconProps extends HTMLAttributes<HTMLElement> {
  animated?: boolean;
  /**
   * Sets the color of the border and the checkmark.
   */
  color: string;
  /** Sets the color of the background. */
  fill?: string;
  size: string;
}

const CompletedCheckMark = styled.div<CheckmarkIconProps>`
  .checkmark-circle {
    stroke-width: 2;
    stroke-miterlimit: 10;
    stroke: ${(props) => props.color};
    fill: ${(props) => props.fill};
    ${(props) =>
      props.animated &&
      css`
        stroke-dasharray: 166;
        stroke-dashoffset: 166;
        animation: ${strokeDraw} 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
      `}
  }
  .checkmark {
    width: ${(props) => props.size};
    height: ${(props) => props.size};
    border-radius: 50%;
    display: block;
    stroke-width: 2;
    stroke: ${(props) => props.color};
    stroke-miterlimit: 10;
    box-shadow: inset 0px 0px 0px ${(props) => props.color};
    transform: scale3d(1.1, 1.1, 1);
    ${(props) =>
      props.animated &&
      css`
        transform: none;
        animation: ${scalePop} 0.3s ease-in-out 0.9s both;
      `}
  }
  .checkmark-check {
    ${(props) =>
      props.animated &&
      css`
        transform-origin: 50% 50%;
        stroke-dasharray: 24;
        stroke-dashoffset: 24;
        animation: ${strokeDraw} 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s
          forwards;
      `}
  }
`;

const CheckmarkIcon: FC<CheckmarkIconProps> = ({
  animated = true,
  color,
  fill = 'none',
  size,
  ...props
}: CheckmarkIconProps) => {
  return (
    <EdnaWrapper>
      <CompletedCheckMark
        color={color}
        fill={fill}
        animated={animated}
        size={size}
        {...props}
      >
        <svg
          role="img"
          className="checkmark"
          viewBox="0 0 26 26"
          width="100%"
          height="100%"
        >
          <title>Checkmark Icon</title>
          <circle
            className="checkmark-circle"
            cx="13"
            cy="13"
            r="12.5"
            fill={fill}
          />
          <path className="checkmark-check" fill={fill} d="M7 13l4 4 8-8" />
        </svg>
      </CompletedCheckMark>
    </EdnaWrapper>
  );
};

export default CheckmarkIcon;
